import { capitalize } from "lodash";
import { toFormat } from "@/lib/dateHelpers";
import type { Person } from "@/types";
import type { DateType } from "react-tailwindcss-datepicker";

export type ReportType = "suspensions" | "terminations";

export interface Variables {
  search?: string;
  suspended?: boolean;
  terminated?: boolean;
  nonPaymentOfDuesTermination?: boolean;
  reestablished?: boolean | null;
  suspendedDateRange?: {
    from: DateType;
    to: DateType;
  };
  terminatedDateRange?: {
    from: DateType;
    to: DateType;
  };
  sectionIds?: string[];
  facilityIds?: string[];
  programTypes?: string[];
  memberClassifications?: string[];
  balanceRange?: {
    from?: number;
    to?: number;
  };
}

type TerminationRecord = {
  code?: string | null;
  startTime?: string | null;
  endTime?: string | null;
};

export type MappedPersonRow = Person & {
  person: {
    firstName?: string | null;
    lastName?: string | null;
    profilePhoto?: string | null;
    mainProgramType?: { name?: string | null };
  };
  status?: string;
  classification?: string;
  section?: string;
  primaryFacility?: string;
  suspensionsDate?: string;
  terminationsDate?: string;
  reestablishedDate?: string;
};

const formatDate = (date?: string | null): string | undefined => (date && toFormat(date)) || undefined;

export const npduTermination = (terminations: TerminationRecord[] = []) => {
  const matches = terminations.filter((t) => t.code === "NON_PAYMENT_OF_DUES");
  if (matches.length === 0) return undefined;
  return [...matches].sort((a, b) => String(b.startTime || "").localeCompare(String(a.startTime || "")))[0];
};

export const mapPeople = (data: any[]): MappedPersonRow[] =>
  (data || []).map(({ updatedAt, dueDate, balance, lineItems, ...person }) => {
    const isDeceased = person.latestProgram?.band?.terminatedReason?.toLowerCase() === "deceased";
    const status =
      person.latestProgram?.status === "TERMINATED" && isDeceased
        ? "Deceased"
        : capitalize(person.latestProgram?.status);
    const npdu = npduTermination(person.terminations || []);

    return {
      ...person,
      person: {
        firstName: person.firstName,
        lastName: person.lastName,
        profilePhoto: person.profilePhoto,
        mainProgramType: {
          name: person.latestProgram?.programType?.name
        }
      },
      status,
      classification: person.latestProgram?.band?.classification?.name,
      section: person.primarySectionAffiliation?.section.name || "",
      primaryFacility: person.employments.filter((e: any) => e.isPrimary)[0]?.facility?.name || "",
      suspensionsDate: formatDate(person.suspensions?.[0]?.startTime),
      terminationsDate: formatDate(npdu?.startTime || person.terminations?.[0]?.startTime),
      reestablishedDate: formatDate(npdu?.endTime)
    };
  });

export const buildGraphqlInput = (variables: Variables) => {
  const { reestablished, ...rest } = variables;
  if (reestablished === null || reestablished === undefined) {
    return rest;
  }
  return { ...rest, reestablished };
};

export const displayName = (row: MappedPersonRow): string =>
  [row.person?.firstName, row.person?.lastName].filter(Boolean).join(" ").trim();
